import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { ToasterService, ToastType } from '~commonInfra/services/ToasterService';
import { Messages } from '~src/codegen/Localize';
import { IDataConnectionInfo } from '~src/datasource/dataConnection/models/IDataConnectionInfo';
import { ISliceable } from '~src/httpClient/fetchers/ISliceable';
import { ListResult } from '~src/httpClient/fetchers/ListResult';
import { FilterFieldNames, FilterOps, IFilterClause, IStringArrayValuedFieldFilterClause, IStringValuedFieldFilterClause } from '~src/spacesPlatform/models/IFilterClause';
import { IOrderClause } from '~src/spacesPlatform/serverApi/types/IOrderClause';

import { FlowAnalyticsExtensionRow } from '../models/FlowAnalyticsExtensionRow';
import { FlowAnalyticsExtensions } from './FlowAnalyticsExtensions';

export function withFlowAnalyticsExtensionConnection(
  connections: ISliceable<IDataConnectionInfo>,
  flowLuid: string,
  filters: ReadonlyArray<IFilterClause>,
  order: ReadonlyArray<IOrderClause>,
): ISliceable<IDataConnectionInfo> {
  return FeatureFlags.prepScriptNodeAnalyticsExtensionsEnabled
    ? new FlowConnectionsQuery(connections, flowLuid, filters, order)
    : connections;
}

export class FlowConnectionsQuery implements ISliceable<IDataConnectionInfo> {
  private selection: Promise<FlowAnalyticsExtensionRow | null>;

  constructor(
    private readonly connections: ISliceable<IDataConnectionInfo>,
    private readonly flowLuid: string,
    private readonly filters: ReadonlyArray<IFilterClause> = [],
    private readonly order: ReadonlyArray<IOrderClause> = [],
  ) {}

  private getSelection(): Promise<FlowAnalyticsExtensionRow | null> {
    if (!this.selection) {
      this.selection = FlowAnalyticsExtensions.getSelection(this.flowLuid).then(connection => connection ? {
        id: `analytics-extension-${connection.connectionLuid}`,
        name: connection.connectionName,
        contentType: 'dataconnection' as const,
        favorite: false,
        updatedAt: null,
        connectionType: connection.connectionType,
        connectionTypeDisplayName: connection.connectionType === 'TABPY' ? `TabPy/${Messages.analyticsExtension()}` : connection.connectionType,
        connectionDetails: null,
        connectionTypeEditable: false,
        connectionOAuth: false,
        testingConnectionAllowed: false,
        usingRemoteQueryAgent: false,
        analyticsExtensionConnection: connection,
        analyticsExtensionFlowLuid: this.flowLuid,
      } : null).catch(() => {
        ToasterService.getInstance().pop({ type: ToastType.Error, text: Messages.flowAnalyticsExtensionConnectionsLoadError() });
        return null;
      });
    }
    return this.selection;
  }

  async getSlice(start: number, size: number): Promise<ListResult<IDataConnectionInfo>> {
    // Inserting one row can shift the requested page by at most one ordinary row.
    // Include that preceding row so sorting remains correct across page boundaries.
    const offset = size > 0 && start > 0 ? 1 : 0;
    const [result, connection] = await Promise.all([
      this.connections.getSlice(start - offset, size + offset),
      this.getSelection(),
    ]);
    if (!connection || !this.matchesFilters(connection)) {
      return offset === 0 ? result : new ListResult(result.items.slice(offset), start, result.total, result.errors, result.moreItems);
    }

    const items = result.items.slice();
    const insertionIndex = items.findIndex(item => this.compare(connection, item) < 0);
    if (size > 0 && start <= result.total) {
      items.splice(insertionIndex < 0 ? items.length : insertionIndex, 0, connection);
    }
    return new ListResult(items.slice(offset, offset + size), start, result.total + 1, result.errors, result.moreItems);
  }

  private matchesFilters(connection: IDataConnectionInfo): boolean {
    return this.filters.every(filter => {
      const { field, value } = filter as IStringValuedFieldFilterClause;
      if (filter.operator === FilterOps.matches && !field) {
        const text = `${connection.name} ${connection.connectionTypeDisplayName}`.toLocaleLowerCase();
        return value.toLocaleLowerCase().split(/\s+/).every(term => text.includes(term));
      }
      // These filters identify the containing flow or constrain ordinary data sources.
      if ([FilterFieldNames.flowId, FilterFieldNames.containerType, FilterFieldNames.isPublished, FilterFieldNames.isEmbedded, FilterFieldNames.includeLiveToDataEngine].includes(field)) {
        return true;
      }
      const actual = field === FilterFieldNames.connectionType ? connection.connectionType
        : field === FilterFieldNames.name ? connection.name : undefined;
      if (actual === undefined) {
        return false;
      }
      if (filter.operator === FilterOps.inArray) {
        return (filter as IStringArrayValuedFieldFilterClause).values.includes(actual);
      }
      return filter.operator === FilterOps.matches
        ? actual.toLocaleLowerCase().includes(value.toLocaleLowerCase())
        : filter.operator === FilterOps.eq && actual === value;
    });
  }

  private compare(left: IDataConnectionInfo, right: IDataConnectionInfo): number {
    for (const { field, ascending } of this.order) {
      if (field !== 'name' && field !== 'connectionTypeDisplayName') {
        continue;
      }
      const comparison = (left[field] || '').localeCompare(right[field] || '', undefined, { sensitivity: 'base' });
      if (comparison !== 0) {
        return ascending ? comparison : -comparison;
      }
    }
    return 0;
  }
}
